import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout, resetState } from '../GlobalAction';
import { SubscriptionPlanTypes } from '../../types/coach';

export interface ICreateCheckoutSession {
  client_secret: string;
}

export interface ICreateCheckoutSessionResponse {
  createCheckoutSession: ICreateCheckoutSession;
}

export interface ICustomerPortalSession {
  url: string;
}

export interface ICreateBillingPortalSession {
  getCustomerPortal: ICustomerPortalSession;
}

export interface ISubscriptionPlans {
  id: string;
  name: SubscriptionPlanTypes;
  stripeProductId: string;
  maxUserCount: number;
  isActive: boolean;
  price: number;
  features: string[];
  rank: number;
}

interface ISubscriptionState {
  createCheckoutSessionLoading: boolean;
  createCheckoutSessionFailed: boolean;
  clientSecret: string | null;
  customerPortalUrl: string | null;
  customerPortalLoading: boolean;
  isFreeTrialActivated: boolean;
  error: string | null;
  getSubscriptionPlansLoading: boolean;
  subscriptionPlans: ISubscriptionPlans[];
  activateFreePlanLoading: boolean;
  activateFreePlanSuccess: boolean;
  unsubscribePlanLoading: boolean;
  unsubscribePlanSuccess: boolean;
}

export interface IdowngradeSubscriptionResponse {
  unsubscribePlan: {
    success: boolean;
    message: string;
    statusCode: number;
  };
}

const initialState: ISubscriptionState = {
  createCheckoutSessionLoading: false,
  createCheckoutSessionFailed: false,
  clientSecret: null,
  customerPortalUrl: null,
  customerPortalLoading: false,
  isFreeTrialActivated: false,
  error: null,
  getSubscriptionPlansLoading: false,
  subscriptionPlans: [],
  activateFreePlanLoading: false,
  activateFreePlanSuccess: false,
  unsubscribePlanLoading: false,
  unsubscribePlanSuccess: false,
};

const subscriptionslice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    createCheckoutSession: (
      state,
      _action: PayloadAction<{ planType?: string; coachId: string }>
    ) => {
      state.createCheckoutSessionLoading = true;
      state.createCheckoutSessionFailed = false;
    },
    createCheckoutSessionSuccess: (state, action: PayloadAction<ICreateCheckoutSession>) => {
      state.createCheckoutSessionLoading = false;
      state.createCheckoutSessionFailed = false;
      state.clientSecret = action.payload.client_secret;
    },
    createCheckoutSessionFailure: (state) => {
      state.createCheckoutSessionFailed = true;
      state.createCheckoutSessionLoading = false;
    },
    clearClientSecret: (state) => {
      state.clientSecret = null;
    },
    createBillingPortalSession: (state, _action: PayloadAction<{ coachId: string }>) => {
      state.customerPortalLoading = true;
    },
    createBillingPortalSessionSuccess: (
      state,
      action: PayloadAction<ICreateBillingPortalSession>
    ) => {
      state.customerPortalLoading = false;
      state.customerPortalUrl = action.payload.getCustomerPortal.url;
    },
    createBillingPortalSessionFailure: (state) => {
      state.customerPortalLoading = false;
    },
    clearBillingSessionUrl: (state) => {
      state.customerPortalUrl = null;
    },
    enableSubscription: (
      _state,
      _action: PayloadAction<{ sessionId: string; coachId: string }>
    ) => {},
    enableSubscriptionSuccess: (_state) => {},
    enableSubscriptionFailure: (_state) => {},
    unSubscribePlan: (state) => {
      state.unsubscribePlanLoading = true;
      state.unsubscribePlanSuccess = false;
    },
    unSubscribePlanSuccess: (state) => {
      state.unsubscribePlanLoading = false;
      state.unsubscribePlanSuccess = true;
    },
    unSubscribePlanFailure: (state) => {
      state.unsubscribePlanLoading = false;
      state.unsubscribePlanSuccess = false;
    },
    resetUnsubscriptionState: (state) => {
      state.unsubscribePlanLoading = false;
      state.unsubscribePlanSuccess = false;
    },
    activateFreeTrial: (state, _action: PayloadAction<{ coachId: string }>) => {
      state.isFreeTrialActivated = false;
    },
    activateFreeTrialSuccess: (state) => {
      state.isFreeTrialActivated = true;
    },
    activateFreeTrialFailure: (state) => {
      state.isFreeTrialActivated = false;
    },
    getSubscriptionPlansRequest: (state, _action: PayloadAction<{ coachId: string }>) => {
      state.getSubscriptionPlansLoading = true;
    },
    getSubscriptionPlansSuccess: (state, action: PayloadAction<ISubscriptionPlans[]>) => {
      state.getSubscriptionPlansLoading = false;
      state.subscriptionPlans = action.payload;
    },
    getSubscriptionPlansFailure: (state, action: PayloadAction<string>) => {
      state.getSubscriptionPlansLoading = false;
      state.error = action.payload;
    },
    activateFreePlan: (state, _action: PayloadAction<{ coachId: string }>) => {
      state.activateFreePlanLoading = true;
      state.activateFreePlanSuccess = false;
    },
    activateFreePlanSuccess: (state) => {
      state.activateFreePlanLoading = false;
      state.activateFreePlanSuccess = true;
    },
    activateFreePlanFailure: (state) => {
      state.activateFreePlanLoading = false;
      state.activateFreePlanSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetState, () => initialState);
    builder.addCase(logout, () => initialState);
  },
});

export const {
  createCheckoutSession,
  createCheckoutSessionSuccess,
  createCheckoutSessionFailure,
  createBillingPortalSession,
  createBillingPortalSessionFailure,
  createBillingPortalSessionSuccess,
  enableSubscription,
  enableSubscriptionSuccess,
  enableSubscriptionFailure,
  clearClientSecret,
  unSubscribePlan,
  unSubscribePlanSuccess,
  unSubscribePlanFailure,
  activateFreeTrial,
  activateFreeTrialSuccess,
  activateFreeTrialFailure,
  getSubscriptionPlansRequest,
  getSubscriptionPlansSuccess,
  getSubscriptionPlansFailure,
  clearBillingSessionUrl,
  activateFreePlan,
  activateFreePlanSuccess,
  activateFreePlanFailure,
  resetUnsubscriptionState,
} = subscriptionslice.actions;

export default subscriptionslice.reducer;
